function buildTileHeightAllowedValues() {
    const values = [5, 10, 15, 20];
    for (let value = 30; value <= 160; value += 10) {
        values.push(value);
    }
    return values;
}

const TILE_HEIGHT_ALLOWED_VALUES = Object.freeze(buildTileHeightAllowedValues());

export function getTileHeightAllowedValues() {
    return Array.from(TILE_HEIGHT_ALLOWED_VALUES);
}

function clampNumber(value, min, max) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return Number(min);
    if (numeric < min) return Number(min);
    if (numeric > max) return Number(max);
    return numeric;
}

function roundToStep(value, step, min = 0) {
    const numericStep = Number(step);
    if (!Number.isFinite(numericStep) || numericStep <= 0) return value;
    const shifted = (Number(value) - Number(min)) / numericStep;
    return Number(min) + Math.round(shifted) * numericStep;
}

function normalizeAllowedValues(rawValues) {
    if (!Array.isArray(rawValues)) return [];
    const unique = new Set();
    rawValues.forEach((raw) => {
        const numeric = Number(raw);
        if (!Number.isFinite(numeric)) return;
        unique.add(Math.round(numeric));
    });
    return Array.from(unique).sort((a, b) => a - b);
}

function snapFromAllowedValues(value, allowedValues, fallback) {
    const allowed = normalizeAllowedValues(allowedValues);
    if (!allowed.length) return Math.round(Number(fallback) || 0);
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return allowed.includes(Number(fallback)) ? Number(fallback) : allowed[0];
    let best = allowed[0];
    let bestDistance = Math.abs(numeric - best);
    for (let i = 1; i < allowed.length; i += 1) {
        const candidate = allowed[i];
        const distance = Math.abs(numeric - candidate);
        if (distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }
    return best;
}

export function snapTileHeight(value, fallback = 50) {
    return snapFromAllowedValues(value, TILE_HEIGHT_ALLOWED_VALUES, fallback);
}

function normalizeSliderValue(slider, value) {
    if (!slider) return value;
    const fallback = slider.defaultValue != null ? slider.defaultValue : slider.min;
    if (slider.inputId === 'thInput' || (Array.isArray(slider.allowedValues) && slider.allowedValues.length)) {
        return snapFromAllowedValues(value, slider.allowedValues || TILE_HEIGHT_ALLOWED_VALUES, fallback);
    }
    const min = Number(slider.min);
    const max = Number(slider.max);
    const clamped = clampNumber(value, min, max);
    const stepped = roundToStep(clamped, slider.step, min);
    const rounded = Number.isInteger(slider.step) ? Math.round(stepped) : stepped;
    return clampNumber(rounded, min, max);
}

export const SLIDERS = {
    scale: {
        inputId: 'scaleInput',
        valueId: 'scaleVal',
        labelId: 'scaleLabel',
        hintId: 'scaleHint',
        label: 'Legacy draft scale (admin)',
        hint: 'Admin-only legacy mode. Standard draft generation is fixed to 1600px long edge.',
        min: 0.5,
        max: 7.0,
        step: 0.1,
        defaultValue: 1.0,
        format: (value) => `${Number(value).toFixed(2)}x`,
    },
    tileHeight: {
        inputId: 'thInput',
        valueId: 'thVal',
        labelId: 'tileHeightLabel',
        hintId: 'tileHeightHint',
        label: 'Base tile height',
        hint: '',
        min: 5,
        max: 160,
        step: 1,
        defaultValue: 50,
        allowedValues: getTileHeightAllowedValues(),
        format: (value) => `${parseInt(value, 10)}px`,
    },
    colorShare: {
        inputId: 'colorShare',
        valueId: 'colorShareVal',
        labelId: 'colorShareLabel',
        hintId: 'colorShareHint',
        label: 'Source photo overlay',
        hint: 'How much color from the source photo is transferred to the tiles.',
        min: 0,
        max: 100,
        step: 1,
        defaultValue: 50,
        format: (value) => String(Math.round(Number(value) || 0)),
    },
    noiseFactor: {
        inputId: 'noiseFactor',
        valueId: 'noiseFactorVal',
        labelId: 'noiseFactorLabel',
        hintId: 'noiseFactorHint',
        label: 'Randomness',
        hint: '0 is strict best-fit matching. Higher values increase how often the app swaps in a random tile from the whole library.',
        min: 0,
        max: 100,
        step: 1,
        defaultValue: 0,
        format: (value) => String(Math.round(Number(value) || 0)),
    },
    reusePenalty: {
        inputId: 'rpInput',
        valueId: 'rpVal',
        labelId: 'reusePenaltyLabel',
        hintId: 'reusePenaltyHint',
        label: 'Repeat control',
        hint: '0 always picks the single best-fit tile. Higher values widen choices to increase variety and reduce repeats.',
        min: 0,
        max: 100,
        step: 1,
        defaultValue: 45,
        format: (value) => String(Math.round(Number(value) || 0)),
    },
};

function applySliderOverrides(slider, overrides) {
    if (!slider || !overrides) return;
    if (overrides.min != null) slider.min = overrides.min;
    if (overrides.max != null) slider.max = overrides.max;
    if (overrides.step != null) slider.step = overrides.step;
    if (overrides.default != null) slider.defaultValue = overrides.default;
    if (Array.isArray(overrides.allowed_values)) {
        slider.allowedValues = normalizeAllowedValues(overrides.allowed_values);
    }
}

export function applyPolicySliders(policySliders = {}) {
    if (!policySliders || typeof policySliders !== 'object') return;
    Object.entries(policySliders).forEach(([key, overrides]) => {
        const slider = SLIDERS[key];
        if (!slider) return;
        applySliderOverrides(slider, overrides);
    });
}

export function getSliderConfigByInputId(inputId) {
    return Object.values(SLIDERS).find((slider) => slider.inputId === inputId) || null;
}

export function getSliderDefaults() {
    return Object.values(SLIDERS).reduce((acc, slider) => {
        acc[slider.inputId] = slider.defaultValue;
        return acc;
    }, {});
}

export function formatSliderValue(sliderKey, value) {
    const slider = SLIDERS[sliderKey];
    if (!slider) return String(value);
    return slider.format ? slider.format(value) : String(value);
}

export function applySliderConfig() {
    Object.values(SLIDERS).forEach((slider) => {
        const input = document.getElementById(slider.inputId);
        if (!input) return;
        const valueEl = document.getElementById(slider.valueId);

        input.min = String(slider.min);
        input.max = String(slider.max);
        input.step = String(slider.step);
        const defaultValue = normalizeSliderValue(slider, slider.defaultValue);
        input.value = String(defaultValue);

        const labelEl = document.getElementById(slider.labelId);
        if (labelEl) updateLabelText(labelEl, slider.label, valueEl);

        const hintEl = document.getElementById(slider.hintId);
        if (hintEl) hintEl.textContent = slider.hint;

        if (valueEl) valueEl.textContent = formatSliderValue(getSliderKey(slider.inputId), defaultValue);

        input.addEventListener('input', () => {
            const normalized = normalizeSliderValue(slider, input.value);
            if (String(normalized) !== String(input.value)) {
                input.value = String(normalized);
            }
            updateSliderDisplay(slider.inputId, normalized);
        });
    });
}

export function updateSliderDisplay(inputId, value) {
    const slider = getSliderConfigByInputId(inputId);
    if (!slider) return;
    const input = document.getElementById(inputId);
    const normalized = normalizeSliderValue(
        slider,
        value != null ? value : (input ? input.value : slider.defaultValue),
    );
    if (input && String(input.value) !== String(normalized)) {
        input.value = String(normalized);
    }
    const valueEl = document.getElementById(slider.valueId);
    if (valueEl) valueEl.textContent = formatSliderValue(getSliderKey(inputId), normalized);
}

function getSliderKey(inputId) {
    const entry = Object.entries(SLIDERS).find(([, slider]) => slider.inputId === inputId);
    return entry ? entry[0] : '';
}

function updateLabelText(labelEl, labelText, valueEl) {
    if (!labelEl) return;
    if (valueEl && valueEl.parentElement === labelEl) {
        const prefix = `${labelText}: `;
        const textNode = Array.from(labelEl.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
        if (textNode) {
            textNode.textContent = prefix;
        } else {
            labelEl.insertBefore(document.createTextNode(prefix), valueEl);
        }
        return;
    }
    labelEl.textContent = labelText;
}
